/**
 * Light/dark mode toggle for the fresh shell.
 *
 * The cabinet ships only one design (fresh). This store flips the
 * `data-theme` attribute on <html> between `fresh` and `fresh-dark`,
 * persisted to localStorage so the choice survives reloads. The
 * `applyToDocument` call at module-load time runs before React paints,
 * so a returning user never sees a flash of the wrong palette.
 */

import { create } from "zustand";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "ns-theme-mode";

function readStored(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "dark" ? "dark" : "light";
}

function applyToDocument(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme =
    mode === "dark" ? "fresh-dark" : "fresh";
}

interface ThemeStore {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  toggle: () => void;
}

export const useThemeMode = create<ThemeStore>((set, get) => ({
  mode: readStored(),
  setMode: (mode) => {
    applyToDocument(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* private mode etc. — fallback to in-memory only */
    }
    set({ mode });
  },
  toggle: () => {
    get().setMode(get().mode === "dark" ? "light" : "dark");
  },
}));

// Apply once on module load so the first paint matches the saved
// preference. The hardcoded `data-theme="fresh"` in index.html is
// only the boot fallback before this script runs.
applyToDocument(readStored());
